import { useCallback, useEffect } from 'react';
import useSmartlook from 'hooks/useSmartlook';

/**
 * Hook for tracking user browser events in Smartlook.
 * Automatically tracks:
 * - Browser window/tab closing
 * - Page refreshing (F5 or refresh button)
 * - Browser navigation (back/forward buttons)
 *
 * @example
 * // In your component:
 * const YourComponent = () => {
 *   useUserBrowserEventsTracking(); // Just call the hook to enable tracking
 *   return <div>Your component content</div>;
 * };
 *
 * @remarks
 * This hook does not track:
 * - Link clicks
 * - Form submissions
 * - Programmatic navigation
 */
const useUserBrowserEventsTracking = () => {
  const { track } = useSmartlook();

  const trackBrowserEvent = useCallback(
    (eventType: 'close' | 'refresh' | 'navigation') => {
      track('browser-event', {
        type: eventType,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    },
    [track],
  );

  useEffect(() => {
    // Track page refresh
    const handleBeforeUnload = () => {
      // Check if it's a refresh (F5 or refresh button)
      if (window.performance?.navigation?.type === 1) {
        trackBrowserEvent('refresh');
      }
    };

    // Track browser window/tab close
    const handleUnload = () => {
      trackBrowserEvent('close');
    };

    // Track browser navigation (back/forward)
    const handlePopState = () => {
      trackBrowserEvent('navigation');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [trackBrowserEvent]);

  return {};
};

export default useUserBrowserEventsTracking;
